/** Milliseconds without a new frame before the display track counts as stalled. */
const FRAME_STALL_THRESHOLD_MS = 2000;

const FRAME_POLL_INTERVAL_MS = 1000;

export interface RecordingAudioGraph {
  readonly context: AudioContext;
  readonly destination: MediaStreamAudioDestinationNode;
  /** Grown after construction when the session's microphone arrives late. */
  readonly sources: AudioNode[];
  readonly playback: AudioNode | null;
}

/**
 * The assistant's audio element, routed through Web Audio once for the life of
 * the session. `createMediaElementSource` binds an element to one node forever,
 * so a route rebuilt per recording throws on a re-share and leaves the element
 * playing into the closed context of the first one — the responder stops
 * hearing the assistant and the next recording has no assistant voice.
 */
export interface PlaybackRoute {
  readonly context: AudioContext;
  readonly element: HTMLAudioElement | null;
  readonly source: AudioNode | null;
}

// test-exception: pure Web Audio graph construction — asserting it needs a real AudioContext, which jsdom does not implement.
export function createPlaybackRoute(
  element: HTMLAudioElement | null
): PlaybackRoute {
  const context = new AudioContext();

  if (element === null) return { context, element, source: null };

  try {
    const source = context.createMediaElementSource(element);

    source.connect(context.destination);

    return { context, element, source };
  } catch (error) {
    console.error(
      'Screen recording could not route the assistant audio',
      error
    );

    return { context, element, source: null };
  }
}

// test-exception: tears down the Web Audio route above; same untestable surface.
export function disposePlaybackRoute(route: PlaybackRoute | null): void {
  if (route === null) return;

  route.source?.disconnect();

  void route.context.close().catch(() => undefined);
}

export interface FrameStallWatch {
  readonly dispose: () => void;
}

/**
 * Screen sharing captures no audio (`getDisplayMedia({ audio: false })`), so the
 * soundtrack is built here: the responder's microphone plus the session's
 * assistant audio route.
 */
// test-exception: pure Web Audio graph construction — asserting it needs a real AudioContext, which jsdom does not implement.
export function createRecordingAudioGraph(args: {
  readonly micStream: MediaStream | null;
  readonly route: PlaybackRoute;
}): RecordingAudioGraph {
  const { context, source: playback } = args.route;
  const destination = context.createMediaStreamDestination();
  const sources: AudioNode[] = [];

  if (args.micStream !== null) {
    const micSource = context.createMediaStreamSource(args.micStream);

    micSource.connect(destination);
    sources.push(micSource);
  }

  playback?.connect(destination);

  return { context, destination, sources, playback };
}

/**
 * Mixes a microphone into a graph that was built without one. The recorder starts
 * the moment the screen is shared, so the session's microphone — a second prompt,
 * answered later — joins the soundtrack in place rather than delaying the start of
 * the recording until it arrives.
 */
// test-exception: pure Web Audio wiring; needs a real AudioContext, which jsdom does not implement.
export function attachMicToRecordingGraph(
  graph: RecordingAudioGraph,
  micStream: MediaStream
): void {
  const micSource = graph.context.createMediaStreamSource(micStream);

  micSource.connect(graph.destination);
  graph.sources.push(micSource);
}

// test-exception: tears down the Web Audio graph above; same untestable surface.
export function disposeRecordingAudioGraph(
  graph: RecordingAudioGraph | null
): void {
  if (graph === null) return;

  for (const source of graph.sources) source.disconnect();

  graph.playback?.disconnect(graph.destination);
}

/**
 * Safari mutes a paused screen share instead of ending it, and does not always
 * fire `mute` — so a stalled frame clock is what tells the session the capture
 * has silently gone blank.
 */
// test-exception: drives requestVideoFrameCallback on a detached <video>; neither exists in jsdom.
export function watchFrameStall(
  screenStream: MediaStream,
  onStalledChange: (isStalled: boolean) => void
): FrameStallWatch {
  const video = document.createElement('video');

  video.srcObject = screenStream;
  video.muted = true;
  video.playsInline = true;
  void video.play().catch(() => undefined);

  let lastFrameAt = Date.now();
  let isActive = true;
  let isStalled = false;

  function report(next: boolean): void {
    if (next === isStalled) return;

    isStalled = next;
    onStalledChange(next);
  }

  function onFrame(): void {
    lastFrameAt = Date.now();
    report(false);

    if (isActive) video.requestVideoFrameCallback(onFrame);
  }

  const supportsFrameCallback =
    'requestVideoFrameCallback' in HTMLVideoElement.prototype;

  if (supportsFrameCallback) video.requestVideoFrameCallback(onFrame);

  const interval = supportsFrameCallback
    ? setInterval(() => {
        if (Date.now() - lastFrameAt > FRAME_STALL_THRESHOLD_MS) report(true);
      }, FRAME_POLL_INTERVAL_MS)
    : null;

  return {
    dispose: () => {
      isActive = false;

      if (interval !== null) clearInterval(interval);

      video.pause();
      video.srcObject = null;
    },
  };
}

/** `preferCurrentTab` is a Chromium-only hint the DOM lib does not declare. */
interface DisplayCaptureOptions extends DisplayMediaStreamOptions {
  readonly preferCurrentTab?: boolean;
}

const DISPLAY_CAPTURE_OPTIONS: DisplayCaptureOptions = {
  video: true,
  audio: false,
  preferCurrentTab: true,
};

// test-exception: reads navigator.mediaDevices; no jsdom implementation to assert against.
export function requestDisplayStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getDisplayMedia(DISPLAY_CAPTURE_OPTIONS);
}

/**
 * How long the recording waits for its audio mix before recording video only.
 *
 * A permission prompt that is ignored, dismissed, or queued behind the screen
 * picker never settles its promise. Awaiting it unbounded held the whole
 * recording — screen already shared — on a microphone that is only ever an
 * enhancement to the footage.
 */
const RECORDING_MIC_TIMEOUT_IN_MS = 3000;

/**
 * Resolves `fallback` if `work` has not settled in time. Used where a pending
 * browser permission prompt would otherwise stall a chain indefinitely: a
 * rejected or slow request is a missing capability, never a stuck session.
 */
export function withTimeout<TValue>(
  work: Promise<TValue>,
  timeoutInMs: number,
  fallback: TValue
): Promise<TValue> {
  return new Promise<TValue>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), timeoutInMs);

    void work
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(fallback);
      });
  });
}

// test-exception: reads navigator.mediaDevices; the bound and the denial branch live in withTimeout, covered directly.
export function requestRecordingMicStream(
  deviceId: string | null,
  timeoutInMs: number = RECORDING_MIC_TIMEOUT_IN_MS
): Promise<MediaStream | null> {
  return withTimeout(
    navigator.mediaDevices
      .getUserMedia({
        video: false,
        audio: deviceId === null ? true : { deviceId: { exact: deviceId } },
      })
      .catch(() => null),
    timeoutInMs,
    null
  );
}
